import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useLiveStreamQueue
 * - Opens SSE to /live?dur=...
 * - Buffers incoming "bits" events (strings like "0101...")
 * - Exposes popByte() so your minute engine can consume 8 bits at a time
 */
export function useLiveStreamQueue(
  { durationMs = 90_000 } = {}
) {
  const [connected, setConnected] = useState(false);
  const [lastSource, setLastSource] = useState(null);
  const [streamError, setStreamError] = useState(null); // Track QRNG errors
  const qRef = useRef([]);       // queue of '0'/'1' chars
  const esRef = useRef(null);
  const positionRef = useRef(0);  // track position in original stream

  // Return buffered BYTES (not bits)
  const bufferedBytes = useCallback(() => Math.floor(qRef.current.length / 8), []);

  // Store ghost byte for consecutive-byte trials (odd trials)
  const ghostByteRef = useRef(null);
  const ghostByteIndexRef = useRef(null);

  // Convert 8 bits to a byte value (0-255)
  const bitsToByteValue = (bits) => {
    let value = 0;
    for (let i = 0; i < 8; i++) {
      if (bits[i] === '1') {
        value |= (1 << (7 - i)); // MSB first
      }
    }
    return value;
  };

  // Trial-based BYTE strategy: odd trials use consecutive bytes, even trials use temporally separated bytes
  // NOTE: Both strategies pull from the SAME quantum stream - no separate QRNG calls
  const popSubjectByte = useCallback((trialNumber) => {
    if (trialNumber % 2 === 1) {
      // Odd trials (1,3,5...): Subject and ghost are CONSECUTIVE in stream (need 16 bits total)
      if (qRef.current.length >= 16) {
        const subjectIndex = positionRef.current;

        // Take 8 bits for subject byte
        const subjectBits = qRef.current.splice(0, 8);
        const subjectByte = bitsToByteValue(subjectBits);

        // Take next 8 bits for ghost byte (store for later)
        const ghostBits = qRef.current.splice(0, 8);
        ghostByteRef.current = bitsToByteValue(ghostBits);
        ghostByteIndexRef.current = positionRef.current + 8; // Ghost gets next 8 indices

        positionRef.current += 16;

        return { byte: subjectByte, rawIndex: subjectIndex };
      }
    } else {
      // Even trials (2,4,6...): Subject now, ghost later - TEMPORALLY SEPARATED in stream
      if (qRef.current.length >= 8) {
        const subjectIndex = positionRef.current;
        const subjectBits = qRef.current.splice(0, 8);
        const subjectByte = bitsToByteValue(subjectBits);
        positionRef.current += 8;

        return { byte: subjectByte, rawIndex: subjectIndex };
      }
    }

    // Log when we can't provide bytes
    console.warn('⚠️ Insufficient bits in queue for byte:', {
      trialNumber,
      isOdd: trialNumber % 2 === 1,
      requiredBits: trialNumber % 2 === 1 ? 16 : 8,
      availableBits: qRef.current.length,
      availableBytes: Math.floor(qRef.current.length / 8)
    });

    return null;
  }, []);

  const popGhostByte = useCallback((trialNumber) => {
    if (trialNumber % 2 === 1) {
      // Odd trials (1,3,5...): Return the stored byte that was consecutive with subject
      const ghostByte = ghostByteRef.current;
      const ghostIndex = ghostByteIndexRef.current;
      ghostByteRef.current = null; // Clear after use
      ghostByteIndexRef.current = null;
      return ghostByte !== null ? { byte: ghostByte, rawIndex: ghostIndex } : null;
    } else {
      // Even trials (2,4,6...): Pull from stream NOW - temporally separated from subject byte
      if (qRef.current.length >= 8) {
        const ghostIndex = positionRef.current;
        const ghostBits = qRef.current.splice(0, 8);
        const ghostByte = bitsToByteValue(ghostBits);
        positionRef.current += 8;
        return { byte: ghostByte, rawIndex: ghostIndex };
      }
    }
    return null;
  }, []);

  const disconnect = useCallback(() => {
    try { esRef.current?.close?.(); } catch { }
    esRef.current = null;
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    disconnect();
    setStreamError(null); // Clear any previous errors
    const es = new EventSource(`/live?dur=${durationMs}`);
    esRef.current = es;
    setConnected(true);

    es.addEventListener("bits", (evt) => {
      const data = JSON.parse(evt.data);  // { ts, source, bits }
      if (data?.source) setLastSource(data.source);
      const s = data?.bits || "";

      // Push each char to the queue
      for (let i = 0; i < s.length; i++) qRef.current.push(s[i]);
    });

    es.addEventListener("error", (evt) => {
      console.error("❌ QRNG error event:", evt);
      try {
        if (evt.data) {
          const data = JSON.parse(evt.data);
          console.error("Error details:", data);
          // Set error state for UI to display
          setStreamError({
            message: data.message || 'Quantum source error',
            detail: data.detail || 'Unknown error'
          });
        }
      } catch (e) {
        console.error("Could not parse error data:", e);
        setStreamError({
          message: 'Quantum source connection error',
          detail: 'Failed to parse error message'
        });
      }
      disconnect();
    });

    es.addEventListener("done", () => {
      disconnect();
    });

    es.onerror = (err) => {
      console.error("❌ EventSource error:", err, "ReadyState:", es.readyState);
      console.error("EventSource URL:", es.url);
      console.error("Full error object:", JSON.stringify(err, null, 2));
      disconnect();
    };
  }, [durationMs, disconnect]);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    connect,
    disconnect,
    popSubjectByte,  // Changed from popSubjectBit
    popGhostByte,    // Changed from popGhostBit
    bufferedBytes,   // Changed from bufferedBits - returns number of complete bytes available
    connected,
    lastSource,
    streamError      // Expose QRNG errors to UI
  };
}
